import { useEffect, useRef, useCallback } from "react";
export function useClockAdapter(options = {}) {
    const { clockAdapter } = options;
    const frameIdRef = useRef(null);
    const callbackRef = useRef(null);
    // requestAnimationFrame wrapper
    const requestFrame = useCallback((callback) => {
        callbackRef.current = callback;
        if (clockAdapter) {
            return clockAdapter.requestFrame((time) => {
                callbackRef.current?.(time);
            });
        }
        // Fallback to native requestAnimationFrame
        if (typeof requestAnimationFrame !== "undefined") {
            return requestAnimationFrame((time) => {
                callbackRef.current?.(time);
            });
        }
        // Fallback to setTimeout
        const now = clockAdapter?.now() ?? Date.now();
        if (typeof setTimeout !== 'undefined') {
            return setTimeout(() => {
                callbackRef.current?.(now);
            }, 1000 / 60);
        }
        return 0;
    }, [clockAdapter]);
    // cancelAnimationFrame wrapper
    const cancelFrame = useCallback((id) => {
        if (clockAdapter) {
            clockAdapter.cancelFrame(id);
        }
        else if (typeof cancelAnimationFrame !== "undefined") {
            cancelAnimationFrame(id);
        }
        else {
            clearTimeout(id);
        }
    }, [clockAdapter]);
    // High-resolution timestamp
    const now = useCallback(() => {
        if (clockAdapter) {
            return clockAdapter.now();
        }
        if (typeof performance !== "undefined" && performance.now) {
            return performance.now();
        }
        return Date.now();
    }, [clockAdapter]);
    // setTimeout wrapper (for timers that don't need frame alignment)
    const setTimeoutWrapper = useCallback((callback, delay) => {
        if (clockAdapter) {
            return clockAdapter.setTimeout(callback, delay);
        }
        // Fallback to native setTimeout
        if (typeof setTimeout !== 'undefined') {
            return setTimeout(callback, delay);
        }
        return 0;
    }, [clockAdapter]);
    const clearTimeoutWrapper = useCallback((id) => {
        if (clockAdapter) {
            clockAdapter.clearTimeout(id);
        }
        else if (typeof clearTimeout !== 'undefined') {
            clearTimeout(id);
        }
    }, [clockAdapter]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (frameIdRef.current !== null) {
                cancelFrame(frameIdRef.current);
                frameIdRef.current = null;
            }
        };
    }, [cancelFrame]);
    return { now, requestFrame, cancelFrame, setTimeout: setTimeoutWrapper, clearTimeout: clearTimeoutWrapper };
}
